import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Optional,
  Inject,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';

// CONFIGS

// COMPONENTS

// INTERFACES

// SERVICES
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import moment from 'moment';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  first,
  skip,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { ClientPaymentProfileFormComponent } from 'v2/modules/client/components/client-payment-profile-form/client-payment-profile-form.component';
import { ECustomFieldObject, ICustomField } from 'v2/modules/custom-field/interfaces/custom-field.interface';
import {
  RfqFormComponent,
  RfqVariantFormComponent,
} from 'v2/modules/rfq/components/';
import {
  CustomFieldApiService,
  LocalStorageKeys,
  PricingLevelApiService,
} from 'v2/modules/shared';
import { DialogConfig } from 'v2/modules/shared/configs/dialog.config';
import { RfqApiService } from 'v2/modules/shared/services/api/rfq/rfq-api.service';
import { toSnakeCaseWithNumberSuffix } from 'v2/modules/shared/util/string-transformation.utils';
import { trackById, trackByValue } from 'v2/modules/shared/util/track-by.util';

import { RfqTableSalesOrderColumnConfig } from '../../../rfq';
import { IChannel } from '../../../shared/interfaces/mtchannel.interface';
import { IColumnConfig } from '../../../shared/interfaces/table.interface';
import { CanEditSalesOrderItemsPipe } from '../../pipes/can-edit-sales-order-items';
import { SalesOrderFacadeService } from '../sales-order-facade.service';

import {
  IBusinessAccountContact,
  ICompany,
} from './../../../client/interfaces/client-company.interface';
import { IClientShipping } from './../../../client/interfaces/client-shipping.interface';
import {
  ESalesOrderStatus,
  ISalesOrder,
} from './../../../order/interfaces/order.interface';
import {
  IPrice,
  IPricingLevel,
} from './../../../pricing/interfaces/pricing.interface';
import { RfqLineItemsFormComponent } from './../../../rfq/components/rfq-line-items-form/rfq-line-items-form.component';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';
import { RfqUtilService } from './../../../shared/services/util/rfq-util.service';
@Component({
  selector: 'lum-sales-order-form',
  templateUrl: './sales-order-form.component.html',
  styleUrls: ['./sales-order-form.component.scss'],
  providers: [CanEditSalesOrderItemsPipe],
})
export class SalesOrderFormComponent implements OnInit, OnDestroy {
  // Form
  form :UntypedFormGroup;

  // Observables
  loading$ :Observable<boolean>;
  rfqs$ :Observable<any[]>; // TODO: Create type
  pricingLevels$ :Observable<IPricingLevel[]>;
  updatingFormFields$ :Observable<boolean>;

  // Form Controls
  scanSearchStr :UntypedFormControl = new UntypedFormControl(null);

  // Data
  rfqs :any[] = [];
  rfqsBySku :any = {};
  pricingByRfq :{ [key :string] :IPrice } = {};
  currentSalesOrder :ISalesOrder = null;
  lineItemsEditDisabledReasonText :string = '';
  lineItemsEditDisabled :boolean = false;

  // State
  loadInitialRfq :boolean = false;
  fromPagination :boolean = false;
  fromScanMode :boolean = false;
  fromEnterEvent :boolean = false;
  title :string = 'Create Sales Order';
  isLineItemEditAllowed :boolean = true;

  rfqTableColumns :IColumnConfig[] = RfqTableSalesOrderColumnConfig;
  customFields :ICustomField[] = [];
  customFieldColumns :IColumnConfig[] = [];
  availableColumns :IColumnConfig[] = [];

  trackById = trackById;
  trackByValue = trackByValue;

  // Search params
  rfqSearchParams :{ sku :string; offset :number; per_page :number } = {
    sku: '',
    offset: 0,
    per_page: 50,
  };

  // Relations
  relation :string = [
    'category',
    'subcategory',
    'basePrice',
    'tags.tag_details',
    'images',
    'locationStock',
    'variantParent.category',
    'variantParent.subcategory',
    'variantParent.tags.tag_details',
    'variantParent.unitOfMeasures',
    'rfqAttachments',
    'unitOfMeasures',
    'quotations',
  ].join(',');

  salesOrderRelations :string = [
    'approval',
    'businessAccount',
    'channel',
    'orderItems.rfq.category',
    'orderItems.rfq.subcategory',
    'orderItems.rfq.basePrice',
    'orderItems.rfq.tags.tag_details',
    'orderItems.rfq.images',
    'orderItems.rfq.locationStock',
    'orderItems.rfq.variantParent.category',
    'orderItems.rfq.variantParent.subcategory',
    'orderItems.rfq.variantParent.tags.tag_details',
    'orderItems.rfq.variantParent.unitOfMeasures',
    'orderItems.rfq.rfqAttachments',
    'orderItems.rfq.unitOfMeasures',
    'orderItems.rfq.quotations',
    'orderItems.customFieldValues',
  ].join('|');

  // Subjects
  private scanSearchUpdated$ = new Subject<string>();
  private onDestroy$ = new EventEmitter<void>();
  private updatingFormFields = new BehaviorSubject<boolean>(false);
  placeholderOrderNumber :string | null = null;
  submitting$ = new BehaviorSubject<boolean>(false);
  private columnConfigFromStorage :IColumnConfig[] = [];

  // TODO: IMPLEMENET FACADE PATTERN
  constructor(
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data :{
      salesOrderId ?:number;
    },
    private cd :ChangeDetectorRef,
    public dialogRef :MatDialogRef<SalesOrderFormComponent>,
    private pricingLevelApiService :PricingLevelApiService,
    private fb :UntypedFormBuilder,
    private rfqUtilService :RfqUtilService,
    private featureToggleService :FeatureToggleService,
    private salesOrderFacadeService :SalesOrderFacadeService,
    private dialog :MatDialog,
    private rfqApiService :RfqApiService,
    private snackBar :MatSnackBar,
    private canEditSalesOrderItemsPipe :CanEditSalesOrderItemsPipe,
    private customFieldApiService :CustomFieldApiService,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.initSalesOrderNumber();
  }

  ngOnDestroy() :void {
    this.salesOrderFacadeService.setCurrentCompany(null);
    this.lineItemsEditDisabledReasonText = '';
    this.lineItemsEditDisabled = false;
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private initSalesOrderNumber() :void {
    if (this?.data) {
      return this.continueInit();
    }

    this.salesOrderFacadeService
      .getNextOrderNumber()
      .pipe(
        first(),
        catchError((error) => {
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response) {
          this.placeholderOrderNumber = response;
        }

        this.continueInit();
      });
  }

  private continueInit() :void {
    this.salesOrderFacadeService.updateState({
      submitting: false,
      currentSalesOrder: null,
    });
    this.initSalesOrderLineItemColumns();
    this.initForm();
    this.initActions();
    this.getFromService();
    this.getContacts().subscribe();
    this.getAddresses().subscribe();
    this.getRfqs().subscribe();
    this.onScanSearchUpdate().subscribe();
    this.getPricingByRfq().subscribe();
    this.getCurrentSalesOrder().subscribe();

    if (this?.data) {
      this.salesOrderFacadeService
        .getById({
          id: this.data?.salesOrderId,
          params: {
            relations: this.salesOrderRelations,
          },
        })
        .subscribe();
      this.title = 'Edit Sales Order';
    }
  }

  handleAddPayMethod(e :Event) {
    e.preventDefault();

    this.dialog
      .open(ClientPaymentProfileFormComponent, {
        data: {
          creditCard: null,
          client: this.currentCompany,
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((response) => {
        if (response?.id) {
          this.snackBar.open('Saved new payment profile', null, {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      });
  }

  handleAddRowItems(items :number) {
    if (this.form && typeof this.lineItemsFormArray.push === 'function') {
      while (items--) {
        this.lineItemsFormArray.push(this.rfqUtilService.emptyItemRow());
      }
    }
  }

  onSelectCompany(company :ICompany) :void {
    this.salesOrderFacadeService.setCurrentCompany(company || null);
  }

  onToggleColumn(event :any, config :IColumnConfig) :void {
    this.rfqTableColumns = this.displayColumns;
    const index = this.rfqTableColumns.findIndex((row) => row.id === config.id);

    if (index >= 0) {
      this.rfqTableColumns[index].visible = event.checked;
      this.initCustomFieldValueFormGroup(event, config);
    }

    localStorage.setItem(
      LocalStorageKeys.SALES_ORDER_LINE_ITEM_FORM_COLUMN_STATE,
      JSON.stringify(this.rfqTableColumns),
    );
  }

  onOpenLineItemsDialog() :void {
    this.dialog
      .open(RfqLineItemsFormComponent, {
        data: {
          warehouse_group_id: this.form.get('warehouse_group_id')?.value,
          client_id: this.form.get('client_id')?.value,
        },
        width: DialogConfig.sizes['xxx-large'],
        height: 'calc(100vh - 150px)',
        disableClose: true,
      })
      .afterClosed()
      .pipe(first())
      .subscribe((lineItems) => {
        if (lineItems) {
          lineItems.forEach((item :any) => {
            this.onAddLineItem({ item });
          });
        }
      });
  }

  // TODO: Move this logic to RfqLineItemsFormComponent
  onAddLineItem(data :{
    item :any;
    index ?:number;
    insertPosition ?:'top' | 'bottom';
  }) :void {
    const { item, index, insertPosition } = data;
    const { quantity, default_selling_unit_id } = item;

    const ordered_uom = item.unit_of_measures.find(
      (uom :any) => uom.id === default_selling_unit_id,
    );

    const itemGroup = this.rfqUtilService.getItemFormGroup(item);

    if (index !== undefined) {
      this.lineItemsFormArray.setControl(index, itemGroup);
    } else {
      // Find the index where the empty/default values end
      let insertIndex = this.lineItemsFormArray.length;
      let emptyCount = 0;
      for (let i = this.lineItemsFormArray.length - 1; i >= 0; i--) {
        const control = this.lineItemsFormArray.at(i);
        const value = control.value;
        if (value.product_name !== '') {
          break;
        }
        emptyCount++;
      }
      if (emptyCount > 0) {
        insertIndex = this.lineItemsFormArray.length - emptyCount;
      }
      this.lineItemsFormArray.insert(
        insertPosition === 'top' ? 0 : insertIndex,
        itemGroup,
      );
    }

    this.salesOrderFacadeService.getByRfq({
      rfqId: item.id.toString(),
      quantity,
      uomId: ordered_uom?.id?.toString(),
      businessAccountId: this.currentCompany?.id?.toString() || '',
      priceLevelId: null,
    });
  }

  onSubmit(e ?:Event) :void {
    e && e.preventDefault();

    const { value } = this.form;
    const {
      orderNumber,
      customerPoNumber,
      orderDate,
      requestedShipDate,
      doNotShipBefore,
      customerNotes,
      internalNotes,
      taxAmount,
      shippingAmount,
      email,
      phone,
      name,
      company,
      street1,
      street2,
      street3,
      city,
      state,
      postalCode,
      country,
      channel,
      line_items,
      isPosted,
      rawShipTo,
      requestedShippingService,
      billTo,
    } = value;

    const payload = {
      orderNumber,
      customerPoNumber,
      orderDate,
      requestedShipDate: requestedShipDate ? moment(requestedShipDate).endOf('day').toISOString() : '',
      doNotShipBefore,
      customerNotes,
      customerEmail: email,
      internalNotes,
      orderTotal: this.calculateOrderTotal(),
      taxAmount,
      totalDiscount: this.lineItemsTotalDiscountAmount,
      shippingAmount,
      companyId: company?.id,
      channelId: channel.id,
      warehouseGroupId: channel?.warehouseGroupId,
      customerUsername: company?.business_name,
      requestedShippingService,
      shipTo: {
        name: name,
        company: company?.business_name ?? rawShipTo?.company,
        street1,
        street2,
        street3,
        city,
        state,
        postalCode,
        country,
        phone,
        email,
      },
      billTo,
      lineItems: line_items
        ?.filter((item :any) => item.product_name && item.sku)
        ?.map((item :any) => {
          const matchedUom = item.unit_of_measures.find(
            (uom :any) => item.ordered_uom_id === uom.id,
          );

          const customFieldValuesArray = Object.values(item.customFieldValues || {})
            .filter(
              (field :any) => field && field.value !== null && field.value !== '',
            )
            .map((field :any) => ({
              custom_field: field.id || null,
              value: field.value,
            }));

          return {
            id: item.id,
            orderItemId: item.item_id,
            lineItemKey: item.sku,
            sku: item.sku,
            name: item.product_name,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            uomId: matchedUom?.id || '',
            uom: matchedUom?.name || '',
            productId: item.item_id,
            discountAmount: item.discount_amount,
            estimatedShipDate: item.estimated_ship_date,
            lineItemStatus: item.line_item_status,
            originalQuantityOrdered: item.original_quantity_ordered,
            customFieldValues: customFieldValuesArray,
          };
        }),
      isPosted,
    };

    if (this.currentSalesOrder) {
      this.submitting$.next(true);
      this.salesOrderFacadeService
        .updateSalesOrder(payload, this.currentSalesOrder.id)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.submitting$.next(false);
            this.dialogRef.close(response);
            this.snackBar.open('Sales Order updated successfully', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
          error: (response) => {
            this.submitting$.next(false);
            this.snackBar.open(
              response?.error || 'Error updating Sales Order',
              'Close',
            );
          },
        });
    } else {
      this.submitting$.next(true);
      this.salesOrderFacadeService
        .createSalesOrder(payload)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.submitting$.next(false);
            this.dialogRef.close(response);
            this.snackBar.open('Sales Order created successfully', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
          error: (response) => {
            this.submitting$.next(false);
            this.snackBar.open(
              response?.error || 'Error creating Sales Order',
              'Close',
            );
          },
        });
    }
  }

  private calculateOrderTotal() :number {
    const lineItemsSubTotal = this.lineItemsFormArray.value
      .filter((item :any) => item.product_name && item.sku)
      .reduce((total :number, item :any) => {
        const lineTotal = (item.quantity * item.unit_price) - (item.discount_amount || 0);
        
return total + lineTotal;
      }, 0);
    
    const shippingAmount = this.form.get('shippingAmount')?.value || 0;
    const taxAmount = this.form.get('taxAmount')?.value || 0;
    
    return lineItemsSubTotal + shippingAmount - taxAmount;
  }

  onOpenSkuDialog() :void {
    const dialogRef = this.dialog.open(RfqFormComponent, {
      data: {
        featureFlag: {
          newKitsStructureEnabled: true,
          lotTrackingEnabled: this.lotTrackingEnabled,
          customFieldsEnabled: this.customFieldsEnabled,
        },
      },
      width: DialogConfig.sizes['xx-large'],
      height: 'calc(100vh - 150px)',
      disableClose: true,
    });

    dialogRef.afterOpened().subscribe(() => {
      import('../../../custom-field').then(({ CustomFieldFormGroupComponent }) => {
        const componentRef =
          dialogRef.componentInstance.customFieldContainerRef.createComponent(
            CustomFieldFormGroupComponent,
          );
        const componentInstance = componentRef.instance;
        componentInstance.customFieldValues = [];
        componentInstance.onChange
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((values) => {
            dialogRef.componentInstance.onCustomFieldChange(values);
          });
        componentInstance.customFieldObject = ECustomFieldObject.RFQ;
      });
    });

    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe();
  }

  onOpenVariantSkuDialog() :void {
    this.dialog
      .open(RfqVariantFormComponent, {
        data: {
          featureFlag: {
            newKitsStructureEnabled: true,
            lotTrackingEnabled: this.lotTrackingEnabled,
          },
        },
        width: DialogConfig.sizes.medium,
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  onSearchRfqs(value :any, scanMode :boolean) :void {
    this.rfqs = [];
    this.loadInitialRfq = true;
    this.fromPagination = false;
    this.fromScanMode = scanMode;
    this.fromEnterEvent = false;
    this.rfqSearchParams = {
      ...this.rfqSearchParams,
      offset: 0,
      sku: value,
    };
    this.scanSearchUpdated$.next(value);
  }

  onSelectSkuFromScanMode(item :any) :void {
    this.onAddLineItems([{ ...item }]);
    this.scanSearchStr.setValue(null);
    this.rfqs = [];
  }

  onAddLineItems(lineItems :any[], index ?:number) :void {
    lineItems.forEach((item :any) => {
      this.addLineItem({ item, index });
    });
    this.cd.detectChanges();
  }

  onSelectShippingAddresss(value :IClientShipping) :void {
    this.form.patchValue({
      street1: value?.address,
      street2: value?.addressLine2,
      street3: value?.addressLine3,
      city: value?.city,
      state: value?.state,
      postalCode: value?.postalCode,
      country: value?.country,
    });
  }

  private addLineItem(data :{
    item :any;
    index ?:number;
    insertPosition ?:'top' | 'bottom';
  }) :void {
    const { item, index, insertPosition } = data;
    const { quantity, default_selling_unit_id } = item;

    const ordered_uom = item.unit_of_measures.find(
      (uom :any) => uom.id === default_selling_unit_id,
    );

    const itemGroup = this.rfqUtilService.getItemFormGroup(item);

    if (index !== undefined) {
      this.lineItemsFormArray.setControl(index, itemGroup);
    } else {
      // Find the index where the empty/default values end
      let insertIndex = this.lineItemsFormArray.length;
      let emptyCount = 0;
      for (let i = this.lineItemsFormArray.length - 1; i >= 0; i--) {
        const control = this.lineItemsFormArray.at(i);
        const value = control.value;
        if (value.product_name !== '') {
          break;
        }
        emptyCount++;
      }
      if (emptyCount > 0) {
        insertIndex = this.lineItemsFormArray.length - emptyCount;
      }
      this.lineItemsFormArray.insert(
        insertPosition === 'top' ? 0 : insertIndex,
        itemGroup,
      );
    }
  }

  private getFromService() :void {
    this.rfqs$ = this.rfqApiService.rfqs$;
    this.pricingLevels$ = this.pricingLevelApiService.pricingLevels$;
    this.loading$ = this.rfqApiService.loading$;
    this.getCustomFields();
  }

  private initActions() :void {
    this.pricingLevelApiService.get();
  }

  private getRfqs() :Observable<any[]> {
    return this.rfqs$.pipe(
      skip(1),
      tap((rfqs :any[] | null) => {
        if (!rfqs) {
          return;
        }

        rfqs.forEach((rfq :any) => {
          this.rfqs.push(rfq);
        });

        if (this.fromPagination) {
          if (rfqs.length === 0) {
            this.snackBar.open('All SKUs successfully loaded.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        } else if (!rfqs?.length && !this.fromEnterEvent) {
          this.snackBar.open('No SKU not found.', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.rfqs = [];
        } else if (this.fromEnterEvent) {
          if (rfqs?.length === 1) {
            const rfq = rfqs[0];
            const rfqsBySku = this.rfqsBySku[rfq.sku?.toLowerCase()];

            if (rfqsBySku) {
              this.onAddLineItems([rfq], rfqsBySku.index);
              delete this.rfqsBySku[rfq.sku?.toLowerCase()];
            }
          } else {
            this.snackBar.open('No SKU not found.', 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            const key = Object.keys(this.rfqsBySku)[0];
            const item = this.rfqsBySku[key]?.item;
            item.get('product_name')?.setValue('');
            this.lineItemsFormArray.setControl(this.rfqsBySku[key]?.index, item);
            this.lineItemsFormArray.updateValueAndValidity();
            delete this.rfqsBySku[key];
          }
          this.rfqs = [];
        } else if (rfqs?.length === 1 && this.fromScanMode) {
          this.onAddLineItems([rfqs[0]]);
          this.scanSearchStr.setValue(null);
          this.rfqs = [];
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onScanSearchUpdate() :Observable<string> {
    return this.scanSearchUpdated$.asObservable().pipe(
      debounceTime(500),
      tap((scanSearchStr :string) => {
        const params :{ [param :string] :string | string[] } = {
          sku: scanSearchStr,
          relations: this.relation,
          per_page: '50',
          offset: '0',
        };
        this.rfqApiService.search(params);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onOverrideChange(override :boolean = false) :void {
    this.form.patchValue({
      overridden: override,
    });
    if (!override) {
      this.lineItemsFormArray.controls.map((control :AbstractControl) => {
        control.patchValue({
          unit_price: control.value.original_unit_price,
          discount_amount: 0,
          discount_percent: 0,
        });
      });
    }
  }

  private initSalesOrderLineItemColumns() :void {
    const columnConfigStorage = localStorage.getItem(
      LocalStorageKeys.SALES_ORDER_LINE_ITEM_FORM_COLUMN_STATE,
    );

    if (columnConfigStorage) {
      this.rfqTableColumns = JSON.parse(columnConfigStorage) as IColumnConfig[];
    }
  }

  private initForm(currentSalesOrder ?:ISalesOrder) :void {
    let defaultItemRows = 5;
    const lineItems = [];
    while (defaultItemRows--) {
      lineItems.push(this.rfqUtilService.emptyItemRow());
    }

    const hasOverride = currentSalesOrder?.orderItems?.some(
      (item) => item.discount > 0,
    );

    this.form = this.fb.group({
      orderNumber: [currentSalesOrder?.orderNumber || ''],
      customerPoNumber: [currentSalesOrder?.customerPoNumber || ''],
      orderDate: [currentSalesOrder?.orderDate || new Date(), Validators.required],
      requestedShipDate: [
        currentSalesOrder?.shipByDate || new Date(),
        Validators.required,
      ],
      doNotShipBefore: [currentSalesOrder?.doNotShipBefore || ''],
      line_items: currentSalesOrder?.orderItems?.length
        ? this.fb.array(
            currentSalesOrder?.orderItems
              .filter((item) => item.ignoreItem === false)
              .map((item) => {
              const itemGroup = this.rfqUtilService.getItemFormGroup(
                this.rfqUtilService.formatItemGroup(item),
              );
              if (item.status !== ESalesOrderStatus.PENDING) {
                itemGroup.disable();
              }

              return itemGroup;
            }),
          )
        : this.fb.array(lineItems),
      customerNotes: [currentSalesOrder?.customerNotes || ''],
      internalNotes: [currentSalesOrder?.internalNotes || ''],

      taxAmount: [currentSalesOrder?.taxAmount || 0],
      discountAmount: [currentSalesOrder?.totalDiscount || 0],
      shippingAmount: [currentSalesOrder?.shippingAmount || 0],
      netOrderTotal: [currentSalesOrder?.orderTotal || 0],

      overridden: [hasOverride || false], // [FR-584] [calvin] allow setting price/discounts

      // Company
      email: [currentSalesOrder?.shipTo?.email || ''],
      phone: [currentSalesOrder?.shipTo?.phone || ''],
      name: [currentSalesOrder?.shipTo?.name || ''],
      company: [currentSalesOrder?.businessAccount || null],
      street1: [currentSalesOrder?.shipTo?.street1 || '', Validators.required],
      street2: [currentSalesOrder?.shipTo?.street2 || ''],
      street3: [currentSalesOrder?.shipTo?.street3 || ''],
      city: [currentSalesOrder?.shipTo?.city || '', Validators.required],
      state: [currentSalesOrder?.shipTo?.state || '', Validators.required],
      postalCode: [
        currentSalesOrder?.shipTo?.postalCode || '',
        Validators.required,
      ],
      country: [currentSalesOrder?.shipTo?.country || '', Validators.required],
      rawShipTo: [currentSalesOrder?.shipTo || {}],
      // Shipping
      shippingAddressId: [null],
      requestedShippingService: [currentSalesOrder?.requestedShippingService || ''],

      // Channel
      channel: [currentSalesOrder?.channel || null, Validators.required],
      price_level_id: [''],
      isPosted: [currentSalesOrder?.isPosted ? 1 : 0, Validators.required],

      // Bill to
    billTo: this.fb.group({
      name: [currentSalesOrder?.billTo?.name || ''],
      company: [currentSalesOrder?.billTo?.company || ''],
      street1: [currentSalesOrder?.billTo?.street1 || ''],
      street2: [currentSalesOrder?.billTo?.street2 || ''],
      city: [currentSalesOrder?.billTo?.city || ''],
      state: [currentSalesOrder?.billTo?.state || ''],
      postalCode: [currentSalesOrder?.billTo?.postalCode || ''],
      country: [currentSalesOrder?.billTo?.country || ''],
      phone: [currentSalesOrder?.billTo?.phone || ''],
      email: [currentSalesOrder?.billTo?.email || ''],
    }),
    });

    // Check if the line_items can be edited by using the CanEditSalesOrderItemsPipe
    
    // if (
    //   (currentSalesOrder &&
    //     !this.canEditSalesOrderItemsPipe.transform(currentSalesOrder))
    // ) {
    //   this.lineItemsEditDisabled = true;
    //   this.form.get('line_items')?.disable();
    //   this.lineItemsEditDisabledReasonText = 'Can only edit order items in PENDING status and created from Luminous or EDI';
    // }

    // FORM VALUE EVENT LISTENERS
    this.listenToCompanyChange();
    this.listenToLineItemsChange();
    this.listenToShippingAmountChange();
    this.listenToPricingLevelChange();
    this.listenToChannelChange();
  }

  private getContacts() :Observable<IBusinessAccountContact[]> {
    return this.contacts$.pipe(
      tap((contacts) => {
        if (contacts?.length) {
          const primaryContact = contacts.find((contact) => contact.primary);
          this.form.patchValue({
            email: primaryContact?.userAccount?.email,
            phone: primaryContact?.userAccount?.phoneOne,
            name: primaryContact?.userAccount?.fullName?.trim(),
          });

          // Patch the bill to address with the primary contact
          this.form.patchValue({
            billTo: {
              name: primaryContact?.userAccount?.fullName?.trim(),
              phone: primaryContact?.userAccount?.phoneOne,
              email: primaryContact?.userAccount?.email,
            },
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getAddresses() :Observable<IClientShipping[]> {
    return this.shippingAddresses$.pipe(
      tap((shippingAddresses) => {
        if (shippingAddresses?.length) {
          const defaultShippingAddress = shippingAddresses.find(
            (address) => address.default,
          );

          if (defaultShippingAddress) {
            this.form.patchValue({
              street1: defaultShippingAddress.address,
              street2: defaultShippingAddress?.addressLine2,
              street3: defaultShippingAddress?.addressLine3,
              city: defaultShippingAddress.city,
              state: defaultShippingAddress.state,
              postalCode: defaultShippingAddress.postalCode,
              country: defaultShippingAddress.country,
            });

            // Patch the bill to address with the shipping address
            this.form.patchValue({
              billTo: {
                street1: defaultShippingAddress.address,
                street2: defaultShippingAddress?.addressLine2,
                street3: defaultShippingAddress?.addressLine3,
                city: defaultShippingAddress.city,
                state: defaultShippingAddress.state,
                postalCode: defaultShippingAddress.postalCode,
                country: defaultShippingAddress.country,
              },
            });
          }
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private listenToCompanyChange() :void {
    this.form
      .get('company')
      ?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.onDestroy$),
      )
      .subscribe((company :ICompany) => {
        this.salesOrderFacadeService.setCurrentCompany(company);

        // Patch the bill to address with the company address
        this.form.patchValue({
          billTo: {
            company: company.business_name,
          },
        });
      });
  }

  private listenToLineItemsChange() :void {
    this.lineItemsFormArray.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((items) => {
        this.updatingFormFields.next(true);
        this.setLineItem(items);
        this.setDiscountEntries();
        this.lineItemsFormArray.markAsDirty();
        this.updatingFormFields.next(false);
      });
  }

  private setLineItem(items :any[]) :void {
    items.forEach((item :any, index :number) => {
      if (!item.sku || !item.sku.length) {
        return;
      }
      const itemForm = this.lineItemsFormArray.at(index) as UntypedFormGroup;
      const discountAmountControl = itemForm.get('discount_amount');
      const discountAmountUnitControl = itemForm.get('discount_amount_per_unit');
      const discountPercentControl = itemForm.get('discount_percent');
      const quantityControl = itemForm.get('quantity');
      const itemNetTotalControl = itemForm.get('net_total_amount');
      const unitPriceControl = itemForm.get('unit_price');
      const unitMeassureControl = itemForm.get('ordered_uom_id');

      // QUANTITY CHANGE
      if (quantityControl?.dirty) {
        if (!this.form.get('overridden')?.value) {
          this.getRfqById(item);
        }
      }

      // UNIT PRICE CHANGE
      if (unitPriceControl?.dirty) {
        this.setDiscountPercentLineItem({
          discountPercentControl,
          discountAmountUnitControl,
          discountAmountControl,
          item,
        });
      }

      // DISCOUNT PERCENT CHANGE
      if (discountPercentControl?.dirty || quantityControl?.dirty) {
        this.setDiscountAmountLineItem({
          discountPercentControl,
          discountAmountUnitControl,
          discountAmountControl,
          item,
        });

        quantityControl?.markAsPristine();
      }

      // DISCOUNT AMOUNT CHANGE
      if (discountAmountControl?.dirty || quantityControl?.dirty) {
        this.setDiscountPercentLineItem({
          discountPercentControl,
          discountAmountUnitControl,
          discountAmountControl,
          item,
        });
      }

      // UOM CHANGE
      if (unitMeassureControl?.dirty) {
        this.getRfqById(item);
      }

      itemNetTotalControl?.patchValue(
        Number(
          (
            quantityControl?.value * unitPriceControl?.value -
            discountAmountControl?.value
          ).toFixed(2),
        ),
        { emitEvent: false },
      );
      discountAmountControl?.markAsPristine();
      discountAmountUnitControl?.markAsPristine();
      discountPercentControl?.markAsPristine();
      quantityControl?.markAsPristine();
      itemNetTotalControl?.markAsPristine();
      unitPriceControl?.markAsPristine();
      unitMeassureControl?.markAsPristine();
    });
  }

  private setDiscountPercentLineItem(data :{
    discountPercentControl :AbstractControl | null;
    discountAmountUnitControl :AbstractControl | null;
    discountAmountControl :AbstractControl | null;
    item :any;
  }) :void {
    const { discountPercentControl, discountAmountUnitControl, item } = data;
    if (item.discount_amount === null || !item.quantity || !item.unit_price) {
      discountPercentControl?.patchValue(0);

      return;
    }
    const discountPercentAmount = Number(
      ((item.discount_amount / (item.quantity * item.unit_price)) * 100).toFixed(2),
    );
    const discountAmount = Number(item.discount_amount.toFixed(2));
    discountPercentControl?.patchValue(
      isNaN(discountPercentAmount) ? 0 : discountPercentAmount,
      { emitEvent: false },
    );
    discountAmountUnitControl?.patchValue(
      isNaN(discountAmount) ? 0 : discountAmount,
      {
        emitEvent: false,
      },
    );
  }

  private setDiscountAmountLineItem(data :{
    discountPercentControl :AbstractControl | null;
    discountAmountUnitControl :AbstractControl | null;
    discountAmountControl :AbstractControl | null;
    item :any;
  }) :void {
    const { discountAmountUnitControl, discountAmountControl, item } = data;
    if (item.discount_percent === null || !item.quantity || !item.unit_price) {
      discountAmountControl?.patchValue(0);

      return;
    }

    const amount = Number(
      ((item.discount_percent / 100) * (item.quantity * item.unit_price)).toFixed(
        2,
      ),
    );
    discountAmountControl?.patchValue(isNaN(amount) ? 0 : amount, {
      emitEvent: false,
    });
    discountAmountUnitControl?.patchValue(isNaN(amount) ? 0 : amount, {
      emitEvent: false,
    });
  }

  private setDiscountEntries() :void {
    const totalAmountControl = this.form.get('total_amount');

    if (totalAmountControl && this.netOrderTotalControl) {
      totalAmountControl.patchValue(this.lineItemsSubTotal, {
        emitEvent: false,
      });
      totalAmountControl.markAsDirty();
    }
    if (this.totalPaid) {
      const netTotal =
        this.lineItemsSubTotal + (this.shippingAmountControl.value || 0);
      let taxAmountValue :number = 0;

      if (
        this.currentCompany &&
        !this.currentCompany?.tax_exempt &&
        this.currentCompany?.tax_rate > 0
      ) {
        taxAmountValue =
          this.lineItemsSubTotal * (this.currentCompany.tax_rate / 100);
        this.form
          .get('taxAmount')
          ?.patchValue(taxAmountValue.toFixed(4), { emitEvent: false });
      }

      this.netOrderTotalControl.patchValue(netTotal - taxAmountValue, {
        emitEvent: false,
      });

      this.netOrderTotalControl.markAsDirty();
    }
  }

  private listenToShippingAmountChange() :void {
    this.shippingAmountControl?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((amount :number) => {
        this.netOrderTotalControl?.patchValue(this.lineItemsSubTotal + amount);
        this.setDiscountEntries();
      });
  }

  private getRfqById(item :any) :void {
    if (item) {
      this.salesOrderFacadeService.getByRfq({
        rfqId: item.item_id.toString(),
        quantity: item.quantity,
        uomId: item?.ordered_uom_id?.toString(),
        businessAccountId: this.currentCompany?.id?.toString() || '',
        priceLevelId: this.pricingLevelIdControl?.value || null,
      });
    }
  }

  private listenToPricingLevelChange() :void {
    this.pricingLevelIdControl?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.lineItemsFormArray?.value?.length) {
          // Recalculate line items
          this.lineItemsFormArray.value.forEach((item :any) => {
            this.getRfqById(item);
          });
        }
      });
  }

  private getPricingByRfq() :Observable<{
    [key :string] :IPrice;
  }> {
    return this.pricingByRfq$.pipe(
      tap((pricingByRfq) => {
        this.pricingByRfq = pricingByRfq;
        Object.keys(pricingByRfq).forEach((itemId :string) => {
          this.updateLineItemPrice(pricingByRfq[itemId]);
        });
        this.setDiscountEntries();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private updateLineItemPrice(rfqPricing :IPrice) :void {
    const items :any[] = this.lineItemsFormArray.value;
    const filteredIndexes = items
      .map((item, index) =>
        item.item_id === rfqPricing.requestedRfqId &&
        item.ordered_uom_id === rfqPricing.requestedUomId
          ? index
          : -1,
      )
      .filter((index) => index !== -1);

    if (filteredIndexes.length === 0) {
      return;
    }

    filteredIndexes.forEach((index) => {
      const item = items[index];
      const itemForm = this.lineItemsFormArray.at(index) as UntypedFormGroup;
      const unitPriceControl = itemForm.get('unit_price');
      const origUnitPriceControl = itemForm.get('original_unit_price');
      const netTotalAmountControl = itemForm.get('net_total_amount');

      origUnitPriceControl?.patchValue(
        Number(rfqPricing.wholesalePrice?.toFixed(2)),
      );

      netTotalAmountControl?.patchValue(
        Number(rfqPricing.wholesalePrice?.toFixed(2)) * item.quantity,
      );

      if (!item?.is_overridden) {
        unitPriceControl?.patchValue(Number(rfqPricing.wholesalePrice?.toFixed(2)));
      }
    });
  }

  private getCurrentSalesOrder() :Observable<ISalesOrder> {
    return this.salesOrderFacadeService.currentSalesOrder$.pipe(
      takeUntil(this.onDestroy$),
      tap((salesOrder) => {
        if (salesOrder) {
          this.currentSalesOrder = salesOrder;
          this.initForm(salesOrder);
          this.placeholderOrderNumber = `LUM-${salesOrder.id}`;
          this.cd.detectChanges();
        }
      }),
    );
  }

  private getCustomFields() :void {
    this.customFieldApiService
      .getAll({
        customFieldObject: ECustomFieldObject.SALES_ORDER_ITEM,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.customFields = response;

          this.customFieldColumns =
            response?.map((field :ICustomField) => ({
              id: toSnakeCaseWithNumberSuffix(field.label, field.id),
              label: field.label,
              visible: false,
              type: field.type,
              customField: true,
              customFieldId: field.id,
              options: field.options,
            })) || [];
        },
        error: (error) => {
          this.snackBar.open(error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initCustomFieldValueFormGroup(event :any, config :IColumnConfig) :void {
    this.displayColumns
      ?.filter((column) => column.customField)
      ?.forEach((column :IColumnConfig, colIndex :number) => {
        if (column.customFieldId === config.customFieldId) {
          const lineItems = this.lineItemsFormArray.controls;
          lineItems.forEach((lineItemControl :AbstractControl) => {
            const lineItemGroup = lineItemControl as UntypedFormGroup;
            const lineItem = lineItemGroup.value;
            if (
              !lineItem.customFieldValues ||
              typeof lineItem.customFieldValues !== 'object'
            ) {
              lineItem.customFieldValues = {};
            }

            const fieldKey = config.customFieldId.toString();
            if (event.checked && !lineItem.customFieldValues[fieldKey]) {
              lineItem.customFieldValues[fieldKey] = {
                id: config.customFieldId,
                value: null,
              };
            }

            let customFieldValuesGroup = lineItemGroup.get(
              'customFieldValues',
            ) as UntypedFormGroup;
            if (
              !customFieldValuesGroup ||
              !(customFieldValuesGroup instanceof UntypedFormGroup)
            ) {
              customFieldValuesGroup = this.fb.group({});
              lineItemGroup.setControl('customFieldValues', customFieldValuesGroup);
            }

            const customFieldKeys = Object.keys(lineItem.customFieldValues || {});
            if (customFieldKeys.length === 0) {
              return;
            }

            customFieldKeys.forEach((key) => {
              if (!customFieldValuesGroup.get(key)) {
                customFieldValuesGroup.addControl(
                  key,
                  this.fb.group({
                    id: [lineItem.customFieldValues[key]?.id || null],
                    value: [lineItem.customFieldValues[key]?.value || null],
                  }),
                );
              }
            });

            customFieldValuesGroup.patchValue(lineItem.customFieldValues);
          });

          this.lineItemsFormArray.markAsDirty();
        }
      });
  }

  get orderDateFormControl() :UntypedFormControl {
    return this.form.get('orderDate') as UntypedFormControl;
  }

  get requestedShipDateFormControl() :UntypedFormControl {
    return this.form.get('requestedShipDate') as UntypedFormControl;
  }

  get doNotShipBeforeFormControl() :UntypedFormControl {
    return this.form.get('doNotShipBefore') as UntypedFormControl;
  }

  get companyFormControl() :UntypedFormControl {
    return this.form.get('company') as UntypedFormControl;
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('line_items') as UntypedFormArray;
  }

  get countryFormControl() :UntypedFormControl {
    return this.form.get('country') as UntypedFormControl;
  }

  get channelFormControl() :UntypedFormControl {
    return this.form.get('channel') as UntypedFormControl;
  }

  get orderTotal() :number {
    // Calculate the total of all line items
    return this.lineItemsFormArray.controls.reduce((total, control) => {
      const item = control.value;
      const discountAmount = Number(item.discount_amount);
      const price =
        Number(item.unit_price) * Number(item.quantity) - discountAmount;

      return total + price;
    }, 0);
  }

  get totalPaid() :number {
    return (
      Number(this.orderTotal) +
      // Number(this.form.get('taxAmount').value || 0) + // commented out, will follow backend calculation orderTotal = lineItemsTotal + shippingAmount
      Number(this.form.get('shippingAmount').value || 0)
    );
  }

  get currentCompany$() :Observable<ICompany | null> {
    return this.salesOrderFacadeService.currentCompany$;
  }

  get contacts$() :Observable<IBusinessAccountContact[]> {
    return this.salesOrderFacadeService.businessAccountsContacts$;
  }

  get shippingAddresses$() :Observable<IClientShipping[]> {
    return this.salesOrderFacadeService.businessAccountShippingAddresses$;
  }

  get currentCompany() :ICompany | null {
    return this.salesOrderFacadeService.currentCompany;
  }

  // get rfqs$(): Observable<any[]> {
  //   return this.rfqApiService.rfqs$;
  // }

  get pricingByRfq$() :Observable<{
    [key :string] :IPrice;
  }> {
    return this.salesOrderFacadeService.pricingByRfq$;
  }

  get customFieldsEnabled() :boolean {
    return this.featureToggleService.featureEnabled(EFeatureFlag.CUSTOM_FIELDS);
  }

  get lotTrackingEnabled() :boolean {
    return this.featureToggleService.featureEnabled(EFeatureFlag.LOT_TRACKING);
  }

  get netOrderTotalControl() :UntypedFormControl {
    return this.form.get('netOrderTotal') as UntypedFormControl;
  }

  get lineItemsTotalDiscountAmount() :number {
    return this.lineItemsFormArray?.value
      ?.map((item :any) => item.discount_amount)
      .reduce(
        (accumulator :number, currentValue :number) =>
          Number(accumulator || 0) + Number(currentValue || 0),
        0,
      );
  }

  get lineItemsSubTotal() :number {
    return this.lineItemsFormArray.value
      .map((item :any) => {
        return item.quantity * item.unit_price - item.discount_amount;
      })
      .reduce(
        (accumulator :number, currentValue :number) => accumulator + currentValue,
        0,
      );
  }

  get shippingAmountControl() :UntypedFormControl {
    return this.form.get('shippingAmount') as UntypedFormControl;
  }

  get channelControl() :UntypedFormControl {
    return this.form.get('channel') as UntypedFormControl;
  }

  get netOrderTotal() :number {
    return this.form.get('netOrderTotal')?.value || 0;
  }

  get isOverride() :boolean {
    return this.form.get('overridden')?.value || false;
  }

  get pricingLevelIdControl() :UntypedFormControl {
    return this.form.get('price_level_id') as UntypedFormControl;
  }

  get displayColumns() :IColumnConfig[] {
    const columnConfigStorage = localStorage.getItem(
      LocalStorageKeys.SALES_ORDER_LINE_ITEM_FORM_COLUMN_STATE,
    );
    const savedColumns = columnConfigStorage
      ? JSON.parse(columnConfigStorage) as IColumnConfig[]
      : [];

    // Separate saved columns into custom vs. system columns
    const savedSystemColumnsMap = new Map(
      savedColumns
        .filter((col) => !col.customField && !col.customFieldId)
        .map((col) => [col.id, col]),
    );
    const savedCustomColumnsMap = new Map(
      savedColumns
        .filter((col) => col.customField && col.id !== null)
        .map((col) => [col.id, col]),
    );

    const mergedSystemColumns = this.rfqTableColumns
      .filter((col) => !col.customField && !col.customFieldId)
      .map((col) => {
        const saved = savedSystemColumnsMap.get(col.id);
        
return {
          ...col,
          visible: saved && 'visible' in saved
            ? saved.visible
            : col.visible ?? false,
        };
      });

    const uniqueCustomFields = Array.from(
      new Map(this.customFieldColumns.map(field => [field.id, field])).values(),
    );
    const mergedCustomFields = uniqueCustomFields.map((field) => {
      const saved = savedCustomColumnsMap.get(field.id);
      
return {
        ...field,
        visible: saved && 'visible' in saved
          ? saved.visible
          : field.visible ?? false,
      };
    });

    this.availableColumns = [...mergedSystemColumns, ...mergedCustomFields];
    
return this.availableColumns;
  }

  get displayColumnCount() :number {
    return this.displayColumns.filter((row) => row.visible).length || 0;
  }

  get hasApprovalFlow() :boolean {
    return !!this.currentSalesOrder?.approval;
  }

  private listenToChannelChange() :void {
    this.channelControl?.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        takeUntil(this.onDestroy$),
      )
      .subscribe((channel :IChannel) => {
        // Reset RFQs
        this.rfqs = [];
        this.rfqsBySku = {};

        this.rfqApiService.setRfqs(null);

        this.getUpdatedStocksForSelectedLineItems();
      });
  }

  private getUpdatedStocksForSelectedLineItems() :void {
    const params = {
      rfqIds: this.lineItemsFormArray.value
        .filter((item :any) => item.sku.trim().length > 0)
        .map((item :any) => item.item_id)
        .join(','),
      warehouse_group_id: this.channelControl.value?.warehouseGroupId,
      relations: 'locationStock',
      sellable: '1',
    };

    if (!params.rfqIds.length) {
      return;
    }
    this.rfqApiService.searchAsync(params).subscribe((response) => {
      // Update each line item with the matching response data
      this.lineItemsFormArray.controls.forEach((control) => {
        const itemId = control.get('item_id')?.value;
        const matchingRfq = response.find((rfq :any) => rfq.id === itemId);

        if (matchingRfq) {
          // Update only the location_stock field while preserving other values
          control.patchValue(
            {
              location_stock: matchingRfq.location_stock,
            },
            { emitEvent: false },
          );
        }
      });

      // Mark form as dirty and trigger change detection
      this.lineItemsFormArray.markAsDirty();
      this.cd.markForCheck();
    });
  }
}
